import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Flame, Sparkles, MessageCircle, Info, Gift, Copy, Share2, Star, ArrowRight, Award, Image as ImageIcon, Compass, RefreshCw, Loader2, Pin, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const R = 58;
const CIRC = 2 * Math.PI * R;

function Gauge({ score, onTrack }) {
  const color = onTrack ? "#34d399" : "#fb923c";
  return (
    <div className="relative" style={{ width: 148, height: 148 }} data-testid="streak-gauge">
      <svg width="148" height="148" viewBox="0 0 148 148" className="-rotate-90">
        <circle cx="74" cy="74" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        <motion.circle
          cx="74" cy="74" r={R} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={CIRC}
          initial={{ strokeDashoffset: CIRC }}
          animate={{ strokeDashoffset: CIRC * (1 - Math.min(100, score) / 100) }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          style={{ filter: `drop-shadow(0 0 8px ${color}66)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="font-display text-4xl font-semibold" style={{ color }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          data-testid="streak-score"
        >
          {score}%
        </motion.span>
      </div>
    </div>
  );
}

const LBL = {
  fr: { title: "Ta curiosité du jour", didYouKnow: "Le saviez-vous ?", capsule: "Capsule de curiosité", another: "Une autre pépite", surprise: "Ta pépite du jour 🌟", qTitle: "La question du jour", qEyebrow: "La Boussole de l'Inattendu", creuser: "Creuser cette question", openBoussole: "Explorer la Boussole", pinnedTitle: "Tes outils", pinnedEmpty: "Épingle tes outils préférés (icône épingle en haut d'un outil) pour les retrouver ici." },
  en: { title: "Your curiosity of the day", didYouKnow: "Did you know?", capsule: "Curiosity capsule", another: "Another gem", surprise: "Your gem of the day 🌟", qTitle: "Question of the day", qEyebrow: "The Compass of the Unexpected", creuser: "Dig into this question", openBoussole: "Explore the Compass", pinnedTitle: "Your tools", pinnedEmpty: "Pin your favourite tools (pin icon at the top of a tool) to find them here." },
  es: { title: "Tu curiosidad del día", didYouKnow: "¿Sabías que?", capsule: "Cápsula de curiosidad", another: "Otra joya", surprise: "Tu joya del día 🌟", qTitle: "La pregunta del día", qEyebrow: "La Brújula de lo Inesperado", creuser: "Profundizar esta pregunta", openBoussole: "Explorar la Brújula", pinnedTitle: "Tus herramientas", pinnedEmpty: "Fija tus herramientas favoritas (icono de chincheta arriba de una herramienta) para encontrarlas aquí." },
  nl: { title: "Je nieuwsgierigheid van de dag", didYouKnow: "Wist je dat?", capsule: "Nieuwsgierigheidscapsule", another: "Nog een pareltje", surprise: "Jouw pareltje van de dag 🌟", qTitle: "De vraag van de dag", qEyebrow: "Het Kompas van het Onverwachte", creuser: "Deze vraag uitdiepen", openBoussole: "Ontdek het Kompas", pinnedTitle: "Jouw tools", pinnedEmpty: "Pin je favoriete tools (pin-icoon bovenaan een tool) om ze hier terug te vinden." },
};
const CAPSULE_KINDS = ["myth", "how", "origin"];

export default function Accueil({ streak, onNavigate, onExplore, pinnedTools = [], onUnpin }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lbl = LBL[(i18n.language || "fr").slice(0, 2)] || LBL.fr;
  const [showTip, setShowTip] = useState(false);
  const [referral, setReferral] = useState(null);
  const [paths, setPaths] = useState([]);
  const [daily, setDaily] = useState(null);
  const [dailyQ, setDailyQ] = useState(null);
  const [capsule, setCapsule] = useState(null);
  const [capsuleLoading, setCapsuleLoading] = useState(false);

  useEffect(() => {
    const lang = (i18n.language || "fr").slice(0, 2);
    const kind = CAPSULE_KINDS[Math.floor(Math.random() * CAPSULE_KINDS.length)];
    api.get(`/fact?lang=${lang}`).then(({ data }) => setDaily(data)).catch(() => {});
    api.get(`/public/capsule?kind=${kind}&lang=${lang}`).then(({ data }) => setCapsule({ ...data, kind })).catch(() => {});
    api.get(`/curiosity/daily?lang=${lang}`).then(({ data }) => setDailyQ(data)).catch(() => {});
  }, [i18n.language]);

  // Capsule surprise — un toast doux, une fois par jour et par session
  useEffect(() => {
    if (!capsule) return;
    const key = `cocoon_capsule_surprise_${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const id = setTimeout(() => {
      toast(lbl.surprise, { description: capsule.heading, duration: 7000 });
    }, 1400);
    return () => clearTimeout(id);
  }, [capsule, lbl.surprise]);

  const refreshCapsule = () => {
    if (capsuleLoading) return;
    setCapsuleLoading(true);
    const lang = (i18n.language || "fr").slice(0, 2);
    const kind = CAPSULE_KINDS[Math.floor(Math.random() * CAPSULE_KINDS.length)];
    api.get(`/public/capsule?kind=${kind}&lang=${lang}&refresh=true`)
      .then(({ data }) => setCapsule({ ...data, kind }))
      .catch(() => {})
      .finally(() => setCapsuleLoading(false));
  };

  useEffect(() => {
    api.get("/referral/me").then(({ data }) => setReferral(data)).catch(() => {});
    api.get("/rabbithole/paths").then(({ data }) => setPaths(data || [])).catch(() => {});
  }, []);

  const refLink = referral ? `${window.location.origin}/?ref=${referral.code}` : "";
  const copyRef = async () => {
    try { await navigator.clipboard.writeText(refLink); toast.success(t("atelier.referralCopied")); }
    catch { toast.error(t("atelier.referralCopied")); }
  };
  const shareRef = async () => {
    const text = t("atelier.referralShareText");
    if (navigator.share) {
      try { await navigator.share({ title: "COCOON", text, url: refLink }); return; } catch { /* cancelled */ }
    }
    copyRef();
  };

  const s = streak || { streak: 0, score: 0, on_track: false, days_to_goal: 5, active_today: false, active_week: 0 };
  const isNewbie = !s.active_today && (s.streak || 0) === 0 && (s.active_week || 0) === 0;
  const starters = t("atelier.starters", { returnObjects: true }) || [];

  let nudge;
  if (s.days_to_goal <= 0) nudge = t("atelier.nudgeDone");
  else if (!s.active_today && s.streak === 0 && s.active_week === 0) nudge = t("atelier.nudgeStart");
  else if (s.active_today) nudge = t("atelier.nudgeToday");
  else nudge = t("atelier.nudgeGoal", { count: s.days_to_goal });

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="accueil-tab">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* greeting */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-10 text-center">
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl italic mb-2" data-testid="accueil-greeting">
            {isNewbie ? t("atelier.welcomeTitle") : t("atelier.greeting", { name: user?.name || "" })}
          </h2>
          <p className="text-foreground/50">{isNewbie ? t("atelier.welcomeSub") : t("atelier.greetingSub")}</p>
        </motion.div>

        {/* La question du jour — La Boussole de l'Inattendu */}
        {dailyQ?.question && (
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="glass frame-pulse rounded-3xl p-7 sm:p-9 mb-8 text-center relative overflow-hidden"
            data-testid="daily-question-card"
          >
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-primary/70 mb-4">
              <Compass size={13} className="animate-[spin_9s_linear_infinite]" /> {lbl.qEyebrow} · {lbl.qTitle}
            </span>
            <p className="font-display italic text-gradient-orange text-2xl sm:text-3xl leading-snug mb-3" data-testid="daily-question-text">
              « {dailyQ.question} »
            </p>
            {dailyQ.spark && (
              <p className="text-foreground/55 leading-relaxed mb-6 max-w-xl mx-auto" data-testid="daily-question-spark">{dailyQ.spark}</p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                data-testid="daily-question-creuser"
                onClick={() => onExplore?.(dailyQ.question)}
                className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"
              >
                {lbl.creuser} <ArrowRight size={17} />
              </button>
              <button
                data-testid="daily-question-open-boussole"
                onClick={() => onNavigate?.("boussole")}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-medium border border-white/12 text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors"
              >
                <Compass size={15} /> {lbl.openBoussole}
              </button>
            </div>
          </motion.div>
        )}

        {/* Ta curiosité du jour — Le saviez-vous + capsule */}
        {(daily?.fact || capsule) && (
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08 }}
            className="glass frame-pulse rounded-3xl p-7 mb-8" data-testid="daily-curiosity-card"
          >
            <p className="font-display text-2xl text-gradient-orange mb-5 flex items-center gap-2">
              <Sparkles size={20} /> {lbl.title}
            </p>
            {daily?.fact && (
              <div className="mb-6">
                <p className="text-primary/80 text-[11px] uppercase tracking-[0.22em] mb-2">{lbl.didYouKnow}</p>
                <p className="text-foreground/85 leading-relaxed font-display text-lg" data-testid="daily-fact-text">{daily.fact}</p>
              </div>
            )}
            {capsule && (
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-5" data-testid="daily-capsule-block">
                <p className="text-primary/80 text-[11px] uppercase tracking-[0.22em] mb-2">{lbl.capsule}</p>
                <p className="font-display text-lg text-foreground/90 mb-2">{capsule.heading}</p>
                <p className="text-foreground/65 text-sm leading-relaxed mb-4 whitespace-pre-wrap">{capsule.body}</p>
                <button onClick={refreshCapsule} disabled={capsuleLoading}
                  className="inline-flex items-center gap-2 text-sm text-primary hover:gap-3 transition-all disabled:opacity-60" data-testid="daily-capsule-refresh">
                  {capsuleLoading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} {lbl.another}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* new-member guided welcome */}
        {isNewbie && (
          <>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="welcome-how">
              {[
                { Icon: MessageCircle, t: t("atelier.how1t"), d: t("atelier.how1") },
                { Icon: Sparkles, t: t("atelier.how2t"), d: t("atelier.how2") },
                { Icon: Star, t: t("atelier.how3t"), d: t("atelier.how3") },
              ].map((h, i) => (
                <div key={i} className="glass rounded-2xl p-6 text-center" data-testid={`welcome-step-${i}`}>
                  <span className="w-11 h-11 rounded-full mx-auto mb-3 flex items-center justify-center bg-primary/10 border border-primary/25">
                    <h.Icon className="text-primary" size={20} strokeWidth={1.6} />
                  </span>
                  <p className="font-display text-lg mb-1">{h.t}</p>
                  <p className="text-foreground/55 text-sm leading-relaxed">{h.d}</p>
                </div>
              ))}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
              className="glass frame-pulse rounded-3xl p-7 mb-8" data-testid="welcome-starters">
              <p className="font-display text-2xl text-gradient-orange mb-1">{t("atelier.starterTitle")}</p>
              <p className="text-foreground/55 text-sm mb-5">{t("atelier.starterHint")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {starters.map((q, i) => (
                  <button key={i} onClick={() => onExplore?.(q)} data-testid={`starter-${i}`}
                    className="group text-left glass rounded-2xl px-5 py-4 flex items-center justify-between gap-3 hover:bg-primary/5 hover:-translate-y-0.5 transition-all">
                    <span className="font-display italic text-foreground/85 leading-snug">{q}</span>
                    <ArrowRight size={17} className="text-primary/60 group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}

        {/* streak module */}
        {!isNewbie && (
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="glass frame-pulse rounded-3xl p-8 mb-8"
          data-testid="streak-module"
        >
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <Gauge score={s.score} onTrack={s.on_track} />
            <div className="flex-1 text-center sm:text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 mb-4" data-testid="streak-status">
                <Flame size={16} className={s.streak > 0 ? "text-orange-400" : "text-foreground/40"} />
                <span className="text-sm text-foreground/80">
                  {s.streak > 0 ? t("atelier.streakActive", { count: s.streak }) : t("atelier.streakNone")}
                </span>
              </div>
              <div className="flex items-center gap-2 justify-center sm:justify-start mb-2 relative">
                <h3 className="font-display text-xl text-foreground/90">{t("atelier.streakTitle")}</h3>
                <button
                  onMouseEnter={() => setShowTip(true)} onMouseLeave={() => setShowTip(false)}
                  onClick={() => setShowTip((v) => !v)}
                  className="text-foreground/40 hover:text-primary transition-colors"
                  data-testid="streak-tooltip-trigger"
                >
                  <Info size={15} />
                </button>
                {showTip && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                    className="absolute top-7 left-0 z-20 glass rounded-xl p-3 text-xs text-foreground/70 max-w-xs leading-relaxed"
                    data-testid="streak-tooltip"
                  >
                    {t("atelier.streakTooltip")}
                  </motion.div>
                )}
              </div>
              <p className="text-foreground/60 leading-relaxed" data-testid="streak-nudge">{nudge}</p>
            </div>
          </div>
        </motion.div>
        )}

        {/* Tes outils — outils épinglés (rectangles identiques) */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} data-testid="pinned-tools-section">
          <p className="uppercase tracking-[0.2em] text-xs text-foreground/40 mb-4 flex items-center gap-2 justify-center sm:justify-start">
            <Pin size={13} className="text-primary/70" /> {lbl.pinnedTitle}
          </p>
          {pinnedTools.length === 0 ? (
            <div className="glass rounded-2xl p-6 text-center border border-white/8" data-testid="pinned-empty">
              <p className="text-foreground/50 text-sm leading-relaxed max-w-md mx-auto">{lbl.pinnedEmpty}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {pinnedTools.map((toolItem) => {
                const Icon = toolItem.icon;
                return (
                  <div key={toolItem.id} className="group relative" data-testid={`pinned-tool-${toolItem.id}`}>
                    <button
                      onClick={() => onNavigate?.(toolItem.id)}
                      data-testid={`pinned-open-${toolItem.id}`}
                      className="w-full h-32 rounded-2xl border border-primary/35 bg-white/[0.035] flex flex-col items-center justify-center gap-3 text-center px-4 hover:border-primary hover:bg-primary/[0.07] hover:-translate-y-1 transition-all"
                      style={{ boxShadow: "inset 0 0 0 1px hsl(38 90% 55% / 0.06)" }}
                    >
                      <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-primary/[0.08] border border-primary/40 text-primary/85 group-hover:text-primary group-hover:border-primary transition-colors">
                        <Icon size={20} strokeWidth={1.7} />
                      </span>
                      <span className="text-sm text-foreground/75 group-hover:text-primary transition-colors line-clamp-2 leading-snug">{toolItem.label}</span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onUnpin?.(toolItem.id); }}
                      data-testid={`pinned-remove-${toolItem.id}`}
                      title={lbl.pinnedTitle}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-background/70 border border-white/15 flex items-center justify-center text-foreground/50 opacity-0 group-hover:opacity-100 hover:text-primary hover:border-primary/50 transition-all"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Mon Chemin — parcours d'exploration du Terrier */}
        {paths.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="glass rounded-3xl p-7 mt-8"
            data-testid="mon-chemin-card"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Compass className="text-primary" size={20} />
              </div>
              <h3 className="font-display text-xl text-gradient-orange">
                {i18n.language?.startsWith("en") ? "My curiosity trail" : i18n.language?.startsWith("es") ? "Mi camino de curiosidades" : i18n.language?.startsWith("nl") ? "Mijn nieuwsgierigheidspad" : "Mon chemin de curiosités"}
              </h3>
            </div>
            <div className="space-y-2.5">
              {paths.slice(0, 6).map((p) => (
                <div key={p.id} className="rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3" data-testid="mon-chemin-item">
                  <p className="text-sm text-foreground/75 leading-relaxed flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    {p.steps.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5">
                        {i > 0 && <ArrowRight size={12} className="text-primary/40" />}
                        <span className={i === p.steps.length - 1 ? "text-primary/80" : ""}>{s}</span>
                      </span>
                    ))}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* referral invite */}
        {referral && (
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }}
            className="glass frame-pulse rounded-3xl p-7 mt-8"
            data-testid="referral-card"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Gift className="text-primary" size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-xl text-gradient-orange mb-1">{t("atelier.referralTitle")}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed mb-1">{t("atelier.referralDesc")}</p>
                <p className="text-primary/80 text-sm leading-relaxed mb-4 flex items-center gap-1.5"><Gift size={14} /> {t("atelier.referralRewardHint")}</p>
                {referral.reward_days_left > 0 && (
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 text-sm mb-4" data-testid="referral-reward-badge">
                    <Gift size={15} /> {t("atelier.referralRewardActive", { count: referral.reward_days_left })}
                  </div>
                )}
                {referral.ambassador ? (
                  <div className="flex mb-4">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/30 text-sm" data-testid="referral-ambassador-badge">
                      <Award size={15} /> {t("atelier.referralAmbassador")}
                    </div>
                  </div>
                ) : (
                  <div className="mb-4" data-testid="referral-tier">
                    <p className="text-xs text-foreground/50 mb-1.5 flex items-center gap-1.5">
                      <Award size={13} className="text-primary/70" />
                      {t("atelier.referralTierProgress", { count: referral.subscribed || 0, goal: referral.tier_goal || 3 })} — {t("atelier.referralTierHint")}
                    </p>
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden max-w-xs">
                      <div className="h-full bg-primary/70 rounded-full transition-all duration-500" style={{ width: `${Math.min(100, ((referral.subscribed || 0) / (referral.tier_goal || 3)) * 100)}%` }} />
                    </div>
                  </div>
                )}
                <div className="rounded-2xl bg-black/30 border border-white/10 px-4 py-3">
                  <p data-testid="accueil-referral-link" className="text-sm text-foreground/80 break-all leading-relaxed select-all">{refLink}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-3">
                  <button data-testid="accueil-referral-copy" onClick={copyRef} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors">
                    <Copy size={15} /> {t("atelier.referralCopy")}
                  </button>
                  <button data-testid="accueil-referral-share" onClick={shareRef} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors">
                    <Share2 size={15} /> {t("atelier.referralShare")}
                  </button>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/brand/referral-card.png?ref=${encodeURIComponent(referral.code || "")}&lang=${(i18n.language || "fr").slice(0, 2)}`}
                    target="_blank" rel="noopener noreferrer" download="cocoon-invitation.png"
                    data-testid="accueil-referral-card"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors"
                  >
                    <ImageIcon size={15} /> {t("atelier.referralSocialCard")}
                  </a>
                </div>
                <p className="text-xs text-foreground/40 mt-3">{t("atelier.referralCount", { count: referral.count })}</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
